import { useState } from "react";
import { Wifi, WifiOff, Loader2 } from "lucide-react";
import { Breadcrumbs } from "@/layouts/Breadcrumbs";
import { Avatar } from "@/components/ui/Avatar";
import { Dropdown } from "@/components/ui/Dropdown";
import { Tooltip } from "@/components/ui/Tooltip";
import { useAuthUser, useLogout } from "@/features/auth/useAuth";
import { useSocketStatus } from "@/hooks/useSocketStatus";
import { ChangePasswordModal } from "@/features/auth/ChangePasswordModal";
import { cn } from "@/utils/cn";

export function Header() {
  const { user } = useAuthUser();
  const logout = useLogout();
  const socketStatus = useSocketStatus();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-cream-50 px-4">
      <Breadcrumbs />

      <div className="flex items-center gap-4">
        <Tooltip
          position="bottom"
          label={
            socketStatus === "connected"
              ? "Realtime connected"
              : socketStatus === "connecting"
                ? "Reconnecting…"
                : "Realtime disconnected"
          }
        >
          <span
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs",
              socketStatus === "connected"
                ? "border-teal-500/40 text-teal-600"
                : "border-danger-500/40 text-danger-500",
            )}
          >
            {socketStatus === "connected" && <Wifi className="h-3.5 w-3.5" />}
            {socketStatus === "connecting" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {socketStatus === "disconnected" && <WifiOff className="h-3.5 w-3.5" />}
            Live
          </span>
        </Tooltip>

        <Dropdown
          trigger={
            <div className="flex items-center gap-2">
              <Avatar name={user?.name ?? "Admin"} size={32} />
              <div className="hidden text-left sm:block">
                <p className="text-sm font-medium leading-tight text-ink">{user?.name}</p>
                <p className="text-xs leading-tight text-ink-faint">{user?.role}</p>
              </div>
            </div>
          }
          options={[
            { label: "Change password", value: "change-password" },
            { label: "Log out", value: "logout", danger: true },
          ]}
          onSelect={(value) => {
            if (value === "change-password") setChangePasswordOpen(true);
            if (value === "logout") logout.mutate();
          }}
        />
      </div>

      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </header>
  );
}
